import type { JSX } from 'react'
import type { ExtOComponent, TAutoplay } from 'video.js'
import type { SkipSegment } from '|>/components/skip-segment/skip-segment.h'
import type { Configuration } from '|>/core'
import type { VideojsBoxRef } from './VideojsBox'

import { memo, useCallback, useEffect } from 'react'
import { Events } from '|>/shared/events'
import { asDepsArray } from '|>/shared/helpers'
import { MAX_AUTO_QUALITY } from '|>/shared/types/quality'
import { useConfiguration } from './hooks/useConfiguration'
import { useEventListener } from './hooks/useEventListener'
import { useEventTrigger } from './hooks/useEventTrigger'
import { useOptions } from './hooks/useOptions'
import { usePlayer } from './hooks/usePlayer'
import { useSource } from './hooks/useSource'
import { applyTogglePopupClass } from './toggle-popup-class'
import { VideojsBox } from './VideojsBox'

import 'video.js/dist/video-js.css'
import '../css/videojs.css'

export type VideojsPlayerProps = {
  using?: ExtOComponent
  sourceUrl?: string
  sourceHeaders?: Record<string, string>
  title?: string
  subtitle?: string
  drm?: any
  contentKey?: string | number
  videoClassName?: string
  onPlayNext?: () => void
  onPlayNextEpisode?: () => void
  onPlayPrevious?: () => void
  onClose?: (_?: undefined, event?: any) => void
  liveProgressBar?: Events.Media.ProgressBarChanged
  onLiveRewind?: (value: number | undefined) => void
  onDelayedLiveRewind?: (value: number | undefined) => void
  onGoLive?: (_?: undefined, event?: any) => void
  onPlaybackEnded?: (endedContentKey?: string | number) => void
  onProgressReachedEnd?: () => void
  isRewound?: boolean
  isRewindPending?: boolean
  rewindDetails?: {
    rewoundTargetStartSec: number
    rewoundUrlStartSec: number
    rewindType?: string
  }
  isAutoLiveTracker?: boolean
  onProgramClick?: (event?: any) => void
  onRedirectTvClick?: (event?: any) => void
  onInfoClick?: (event?: any) => void
  onPlaylistClick?: (event?: any) => void
  skipSegments?: SkipSegment[]
  externalError?: Events.Media.Error
  chromecast?: Configuration['chromecast']
  cmcd?: Configuration['cmcd']
  onReady?: (box: VideojsBoxRef) => void
  isUrlRequestPending?: boolean
  locale?: string
  muted?: boolean
  autoplay?: TAutoplay
  isRenderedChildren?: boolean
  poster?: Events.Controls.SetPoster
  logo?: string
  seekStep?: number
  onPlaying?: () => void
  onTokenExpired?: () => void
  maxAutoQuality?: MAX_AUTO_QUALITY
  dolby?: Configuration['dolby']
  smartlib?: Configuration['smartlib']
  isSmartlibEnabled?: boolean
  isPlayerDisabled?: boolean
}

/**
 * Contrainer component for VideoJS player (wrapper)
 * Handles logic, events and player initialization
 */
export const VideojsPlayer = memo(VideojsPlayer_)
function VideojsPlayer_({
  using,
  sourceUrl,
  sourceHeaders,
  title,
  subtitle,
  drm,
  contentKey,
  liveProgressBar,
  videoClassName,
  onPlayNext,
  onPlayNextEpisode,
  onPlayPrevious,
  onClose,
  onLiveRewind,
  onDelayedLiveRewind,
  onGoLive,
  onPlaybackEnded,
  onProgressReachedEnd,
  isRewound = false,
  isRewindPending = false,
  rewindDetails = {
    rewoundTargetStartSec: 0,
    rewoundUrlStartSec: 0,
  },
  onProgramClick,
  onRedirectTvClick,
  onInfoClick,
  onPlaylistClick,
  skipSegments,
  isAutoLiveTracker = false,
  chromecast,
  cmcd,
  onReady,
  isUrlRequestPending,
  externalError,
  locale,
  muted = false,
  autoplay = 'any',
  isRenderedChildren = false,
  poster,
  logo,
  seekStep,
  onPlaying,
  onTokenExpired,
  maxAutoQuality,
  dolby,
  smartlib: smartlibProp,
  isSmartlibEnabled,
  isPlayerDisabled = false,
}: VideojsPlayerProps): JSX.Element {
  const { player, ref, setPlayer } = usePlayer()
  const options = useOptions({
    language: locale,
    muted,
    autoplay,
    enableLowInitialPlaylist: maxAutoQuality !== MAX_AUTO_QUALITY.HIGH,
  })

  // Create smartlib configuration from prop or isSmartlibEnabled flag
  const smartlib =
    smartlibProp ?? (isSmartlibEnabled ? { enabled: true } : undefined)

  const configuration = useConfiguration({
    using,
    onReady: setPlayer,
    chromecast,
    cmcd,
    dolby,
    smartlib,
  })
  const sources = useSource(sourceUrl, drm?.la, sourceHeaders)

  useEffect(() => {
    if (onReady && player) onReady(ref.current!) // reference should be available at this point
  }, [onReady, player])

  const handlePlaybackEnded = useCallback(() => {
    onPlaybackEnded?.(contentKey)
  }, [contentKey, onPlaybackEnded])

  /*
   * add event listeners to underlying videojs player
   */

  useEventListener(player, Events.Player.Close, onClose)
  useEventListener(player, Events.Player.LiveRewind, onLiveRewind)
  useEventListener(player, Events.Player.DelayedLiveRewind, onDelayedLiveRewind)
  useEventListener(player, Events.Player.GoLive, onGoLive)
  useEventListener(player, 'ended' as any, handlePlaybackEnded)
  useEventListener(player, Events.Player.PlayNext, onPlayNext)
  useEventListener(player, Events.Player.PlayNextEpisode, onPlayNextEpisode)
  useEventListener(player, Events.Player.PlayPrevious, onPlayPrevious)
  useEventListener(player, Events.Player.TokenExpired, onTokenExpired)
  useEventListener(
    player,
    Events.Player.ProgressReachedEnd,
    onProgressReachedEnd
  )
  useEventListener(player, Events.Player.ProgramClick, onProgramClick)
  useEventListener(player, Events.Player.RedirectTvClick, onRedirectTvClick)
  useEventListener(player, Events.Player.InfoClick, onInfoClick)
  useEventListener(player, Events.Player.PlaylistClick, onPlaylistClick)

  useEventListener(player, Events.Player.TogglePopup, (event) =>
    applyTogglePopupClass(player, event)
  )

  // prettier-ignore
  // useEventListener(player, 'waiting' as any, () => console.info('player is waiting'))
  // prettier-ignore
  // useEventListener(player, 'dispose' as any, () => console.info('player is disposed'))
  // prettier-ignore
  useEventListener(player, 'playing' as any, onPlaying)

  /*
   * trigger videojs player events on react component properties change
   */

  // update playing source
  useEventTrigger(player, Events.Media.Load, sources, [sources])

  // update title and subtitle
  useEventTrigger(
    player,
    Events.Media.TitleChanged,
    { title, subtitle, logo },
    [title, subtitle, logo]
  )

  // event that indicates user changed content (title used as indicator)
  useEventTrigger(player, Events.Media.ContentChanged, contentKey, [contentKey])

  // indicates player renders any app component
  useEventTrigger(player, Events.Media.IsRenderedChildren, isRenderedChildren, [
    isRenderedChildren,
  ])

  // update state of live ui
  useEventTrigger(player, Events.Media.IsRewound, isRewound, [isRewound])

  // provide custom seek step
  useEventTrigger(player, Events.Media.SeekStep, seekStep, [seekStep])

  // Rewind details - user clicked stamp, url started from stamp, rewind type
  useEventTrigger(player, Events.Media.RewindDetails, rewindDetails, [
    rewindDetails,
  ])

  // use native live seeking - ignore: IsRewound, isAutoLiveTracker, onGoLive (if app is confident in 'rewind')
  useEventTrigger(player, Events.Media.IsAutoLiveTracker, isAutoLiveTracker, [
    isAutoLiveTracker,
  ])

  // custom adMatcher
  // useEventTrigger(player, Events.Media.AdMatcher, adMatcher, [adMatcher])

  // show play next control button if handler is provided, hide otherwise
  useEventTrigger(
    player,
    Events.Controls.ShowPlayNextControl,
    Boolean(onPlayNext),
    [onPlayNext]
  )

  // show play next episode control button if handler is provided, hide otherwise
  useEventTrigger(
    player,
    Events.Controls.ShowPlayNextEpisodeControl,
    Boolean(onPlayNextEpisode),
    [onPlayNextEpisode]
  )

  // show play previous control button if handler is provided, hide otherwise
  useEventTrigger(
    player,
    Events.Controls.ShowPlayPreviousControl,
    Boolean(onPlayPrevious),
    [onPlayPrevious]
  )

  // show info control button if handler is provided, hide otherwise
  useEventTrigger(
    player,
    Events.Controls.ShowInfoControl,
    Boolean(onInfoClick),
    [onInfoClick]
  )

  // show info control button if handler is provided, hide otherwise
  useEventTrigger(
    player,
    Events.Controls.ShowPlaylistControl,
    Boolean(onPlaylistClick),
    [onPlaylistClick]
  )

  useEventTrigger(player, Events.Media.IsUrlPending, isUrlRequestPending, [
    isUrlRequestPending,
  ])

  useEventTrigger(player, Events.Media.IsRewindPending, isRewindPending, [
    isRewindPending,
  ])

  useEventTrigger(player, Events.Media.Error, externalError, [externalError])

  // update live progress bar boundaries
  useEventTrigger(
    player,
    Events.Media.ProgressBarChanged,
    liveProgressBar,
    asDepsArray(liveProgressBar, 5)
  )

  useEventTrigger(player, Events.Controls.SetPoster, poster, [poster])

  useEventTrigger(player, Events.Media.MaxAutoQuality, maxAutoQuality, [
    maxAutoQuality,
  ])

  useEventTrigger(player, Events.Media.DolbyConfiguration, dolby, [dolby])

  useEventTrigger(player, Events.Media.SkipSegmentsChanged, skipSegments, [
    skipSegments,
  ])

  useEventTrigger(player, Events.Media.IsPlayerDisabled, isPlayerDisabled, [
    isPlayerDisabled,
  ])
  /*
   * render videojs player itself (inside wrapper)
   */

  return (
    <VideojsBox
      ref={ref}
      options={options}
      configuration={configuration}
      className={videoClassName}
    />
  )
}
